import { rotuloFalhaNoEnvio, seloDaParticipacao } from '@/shared/constants/assinaturaRotulos';
import { formatDateTimeAt, formatRelativeTimelineDate } from '@/shared/formatters';
import { pluralizar } from '@/shared/utils/text';

import type {
  AlertaAssinaturaUi,
  DetalheSignatarioUi,
  SeloSignatarioUi,
  SignatarioUi,
} from '../types/assinaturaUiTypes';
import type {
  AssinaturaAcordoAlerta,
  AssinaturaAcordoResumo,
  AssinaturaSignatario,
  AssinaturaSignatarioStatus,
} from '../types/negociacaoDetailTypes';

const UM_MINUTO_MS = 60_000;
const SETE_DIAS_MS = 7 * 24 * 60 * 60 * 1000;

const BADGE_POR_STATUS: Record<AssinaturaSignatarioStatus, SeloSignatarioUi> = {
  ASSINOU: {
    circulo: 'ASSINADO',
    badgeLabel: seloDaParticipacao.ASSINOU.rotulo,
    badgeVariant: seloDaParticipacao.ASSINOU.variante,
  },
  ENVIADO: {
    circulo: 'NA_VEZ',
    badgeLabel: seloDaParticipacao.NA_VEZ.rotulo,
    badgeVariant: seloDaParticipacao.NA_VEZ.variante,
  },
  LINK_ABERTO: {
    circulo: 'NA_VEZ',
    badgeLabel: seloDaParticipacao.NA_VEZ.rotulo,
    badgeVariant: seloDaParticipacao.NA_VEZ.variante,
  },
  AGUARDA_VEZ: {
    circulo: 'AGUARDANDO',
    badgeLabel: seloDaParticipacao.AGUARDANDO.rotulo,
    badgeVariant: seloDaParticipacao.AGUARDANDO.variante,
  },
  FALHA_ENVIO: {
    circulo: 'AGUARDANDO',
    badgeLabel: rotuloFalhaNoEnvio,
    badgeVariant: 'error',
  },
};

const SELO_A_CONFERIR: SeloSignatarioUi = {
  circulo: 'AGUARDANDO',
  badgeLabel: seloDaParticipacao.A_CONFERIR.rotulo,
  badgeVariant: seloDaParticipacao.A_CONFERIR.variante,
};

function detalharSignatario(signatario: AssinaturaSignatario): DetalheSignatarioUi {
  switch (signatario.status) {
    case 'ASSINOU':
      return {
        detalhe: formatDateTimeAt(signatario.assinadoEm) || 'Assinado',
        detalheDot: null,
      };
    case 'LINK_ABERTO':
      return { detalhe: 'Link aberto', detalheDot: 'success' };
    case 'ENVIADO':
      return { detalhe: 'Link não aberto', detalheDot: 'neutral' };
    case 'FALHA_ENVIO':
      return { detalhe: 'Link não entregue', detalheDot: 'neutral' };
    case 'AGUARDA_VEZ':
      return { detalhe: 'Assina em seguida', detalheDot: null };
    default:
      return { detalhe: '', detalheDot: null };
  }
}

export function mapSignatarioParaUi(signatario: AssinaturaSignatario): SignatarioUi {
  const selo = Object.hasOwn(BADGE_POR_STATUS, signatario.status)
    ? BADGE_POR_STATUS[signatario.status]
    : SELO_A_CONFERIR;
  return {
    ...selo,
    ...detalharSignatario(signatario),
    conectorPreenchido: signatario.status === 'ASSINOU',
  };
}

export function formatContadorAssinaturas(acordo: AssinaturaAcordoResumo): string {
  return `${acordo.totalAssinados} de ${acordo.totalSignatarios} assinaram`;
}

export function mapAlertaAssinatura(alerta: AssinaturaAcordoAlerta): AlertaAssinaturaUi {
  const dias = alerta.diasSemAssinatura;
  const tempo = dias <= 0 ? 'hoje' : `há ${pluralizar(dias, 'dia')}`;
  return {
    titulo: 'Assinatura pendente',
    // O core ancora este alerta na abertura da pendência, não no envio do contrato.
    descricao: `Pendência de assinatura aberta ${tempo}.`,
    variant: 'warning',
  };
}

export function formatAtualizacaoPainel(signatarios: AssinaturaSignatario[]): string | null {
  const ultimoEvento = signatarios
    .map((s) => new Date(s.ultimoEventoEm).getTime())
    .filter((t) => !Number.isNaN(t))
    .sort((a, b) => b - a)[0];

  if (ultimoEvento === undefined) return null;

  const idadeMs = Date.now() - ultimoEvento;
  if (idadeMs < UM_MINUTO_MS) return 'Atualizado agora';
  if (idadeMs >= SETE_DIAS_MS) return `Atualizado em ${formatDateTimeAt(new Date(ultimoEvento))}`;

  return `Atualizado ${formatRelativeTimelineDate(new Date(ultimoEvento)).toLowerCase()}`;
}
